import React, { useEffect, useRef, useState } from "react";
import { Languages, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import api, { apiError } from "@/lib/api";
import { useLang } from "@/i18n";

export default function PretranslateButton() {
  const { t } = useLang();
  const [status, setStatus] = useState(null); // { total, done, complete }
  const [busy, setBusy] = useState(false);
  const poll = useRef(null);

  const refresh = async () => {
    try {
      const { data } = await api.get("/guest-languages/pretranslate-status");
      setStatus(data);
      if (data.complete && poll.current) { clearInterval(poll.current); poll.current = null; setBusy(false); }
      return data;
    } catch { /* genegeerd */ }
  };

  useEffect(() => {
    refresh();
    return () => { if (poll.current) clearInterval(poll.current); };
  }, []);

  const start = async () => {
    setBusy(true);
    try {
      await api.post("/guest-languages/pretranslate");
      toast.success(t("pretranslate_started"));
      if (poll.current) clearInterval(poll.current);
      poll.current = setInterval(refresh, 4000);
      refresh();
    } catch (e) {
      setBusy(false);
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  };

  const pct = status && status.total ? Math.round((status.done / status.total) * 100) : 0;
  const complete = status?.complete;

  return (
    <div className="space-y-1.5" data-testid="pretranslate-box">
      <div className="flex items-center gap-3 flex-wrap">
        <Button type="button" variant="outline" size="sm" onClick={start} disabled={busy && !complete}
          data-testid="pretranslate-button">
          {busy && !complete ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Languages className="h-4 w-4 mr-2" />}
          {t("pretranslate")}
        </Button>
        {status && (
          <span className="text-xs text-muted-foreground flex items-center gap-1" data-testid="pretranslate-status">
            {complete ? <><Check className="h-3.5 w-3.5 text-emerald-600" />{t("pretranslate_done")}</>
              : `${status.done}/${status.total} · ${pct}%`}
          </span>
        )}
      </div>
      {status && !complete && status.done > 0 && (
        <div className="h-1.5 w-full max-w-xs rounded-full bg-muted overflow-hidden" data-testid="pretranslate-bar">
          <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
        </div>
      )}
      <p className="text-[11px] text-muted-foreground">{t("pretranslate_hint")}</p>
    </div>
  );
}
